import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TwoFactorStatus } from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   Two-factor authentication, from the dashboard's side.
   ----------------------------------------------------------------------------
   docs/ADMIN-ROADMAP.md 1.2. Opt-in: the specification asked for it to be
   mandatory for the owner and that is deliberately deferred until the flow has
   been used for real, because the owner is the only admin on a live system.
   ========================================================================== */

const KEY = ['two-factor'] as const;

export function useTwoFactor() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<{ twoFactor: TwoFactorStatus }>('/api/auth/2fa')).twoFactor,
  });
}

export interface Enrolment {
  secret: string;
  otpauthUri: string;
  /** `data:image/svg+xml;base64,…`, rendered by the API. The dashboard has
      about 12 KB of weight budget left and a QR encoder is roughly twice
      that, so this arrives ready to put in an `<img>`. */
  qrSvgDataUri: string;
}

export function useBeginEnrolment() {
  return useMutation({
    mutationFn: () => api.post<Enrolment & { ok: true }>('/api/auth/2fa/enrol'),
  });
}

export function useConfirmEnrolment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ recoveryCodes: string[] }>('/api/auth/2fa/confirm', { code }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDisableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => api.post('/api/auth/2fa/disable', { password }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Hand the recovery codes over as a file.
 *
 * They are shown once and never again, and "copy these ten strings somewhere
 * safe" is advice most people mean to follow later. A download is the one
 * action that survives closing the tab by accident.
 *
 * Built in the browser from a value already in memory — no request, nothing
 * written to a server, and the object URL is revoked immediately so the blob
 * does not outlive the click.
 */
export function downloadRecoveryCodes(codes: string[], email: string): void {
  const body = [
    'makeyoursite.in — admin recovery codes',
    `Account: ${email}`,
    `Saved: ${new Date().toISOString().slice(0, 10)}`,
    '',
    'Each code works ONCE. Keep this somewhere a thief would not look;',
    'anyone holding both your password and one of these can sign in.',
    '',
    ...codes,
    '',
  ].join('\n');

  const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'makeyoursite-recovery-codes.txt';
  link.click();
  URL.revokeObjectURL(url);
}
